import { useRef, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Brand, Menu, MENU_ITEM_CLASS, useFocusTrap } from '@truecare/ui';
import { useCurrentUser } from '../auth/use-current-user';
import { useSignOut } from '../auth/sign-out';
import { buildSignInRedirect } from '../lib/portal-redirect';
import { ADMIN_NAV_GROUPS } from './admin-nav-items';

/**
 * Phase 14 (admin-nav follow-up) — admin shell: slim header + grouped left
 * sidebar + content.
 *
 * The 12 admin routes are grouped into a vertical sidebar (see
 * admin-nav-items.ts) rather than crammed into the header bar. Responsive:
 *   - Desktop (md+): the sidebar is persistent alongside the content.
 *   - Mobile (< md): the sidebar collapses; a hamburger in the header opens
 *     the SAME grouped nav as an off-canvas drawer (backdrop + Escape close).
 * One nav renderer (`NavList`) feeds both, so desktop and mobile never drift.
 *
 * Per 14-C the nav rows stay dense (compact, single-line); detail surfaces in
 * `children` keep portal's editorial breathing room. Sign-out bounces to the
 * portal sign-in URL because admin has no unauthenticated surface (14a audit).
 */
export interface AdminLayoutProps {
  children: ReactNode;
}

/** The grouped nav, shared by the desktop sidebar and the mobile drawer. */
function NavList({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav className="flex flex-col gap-5 px-4 py-6 text-sm" aria-label="Admin sections">
      {ADMIN_NAV_GROUPS.map((group) => (
        <div key={group.label} className="flex flex-col gap-1.5">
          <div className="px-2 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-ink-muted">
            {group.label}
          </div>
          <div className="ml-2 flex flex-col gap-0.5 border-l border-border pl-1.5">
            {group.items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                activeOptions={item.exact ? { exact: true } : undefined}
                onClick={onNavigate}
                className="rounded px-2 py-1.5 text-forest transition-colors hover:bg-cream hover:text-gold"
                activeProps={{ className: 'rounded px-2 py-1.5 bg-cream font-medium text-gold' }}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const user = useCurrentUser();
  const signOut = useSignOut();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const drawerCloseRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);

  // audit medium — the drawer moved focus in + closed on Escape but never TRAPPED
  // Tab, so Shift+Tab from the close button escaped into the header controls
  // behind the (AT-inert) backdrop. useFocusTrap adds the Tab cycle AND handles
  // the focus-in + Escape + focus-restore this used to do by hand. (The hook's
  // own docstring cites this very drawer as its precedent.)
  useFocusTrap(drawerRef, { active: drawerOpen, onEscape: () => setDrawerOpen(false) });

  const handleSignOut = () => {
    // Bounce to portal sign-in regardless of POST outcome; buildSignInRedirect()
    // encodes `?next=` back to admin, and the hook's onError clears local auth
    // (14a audit H2) so the operator is signed out even if the call drops.
    const bounce = () => {
      if (typeof window !== 'undefined') {
        window.location.href = buildSignInRedirect('/');
      }
    };
    signOut.mutate(undefined, { onSuccess: bounce, onError: bounce });
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* WCAG 2.4.1 — bypass blocks. First focusable element; visually hidden
          until keyboard-focused, then jumps to the main content region. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-forest focus:px-4 focus:py-2 focus:font-body focus:text-sm focus:font-medium focus:text-paper focus:shadow-lg"
      >
        Skip to main content
      </a>
      <header className="sticky top-0 z-30 border-b border-border bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-4 py-3 md:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <button
              ref={hamburgerRef}
              type="button"
              className="shrink-0 rounded p-1.5 text-forest transition-colors hover:bg-cream md:hidden"
              aria-label="Open navigation"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
                <path
                  d="M3 5h14M3 10h14M3 15h14"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <div className="flex min-w-0 items-baseline gap-2">
              {/* Signed-in convention: logo → app home (the admin dashboard,
                  route '/'), matching the portal shell's signed-in behavior.
                  Admin has no signed-out surface (sign-in bounces to the portal),
                  so there's no marketing-home branch here. */}
              <Link
                to="/"
                className="inline-flex shrink-0 items-baseline gap-1.5"
                aria-label="TrueCare™ Partners — home"
              >
                {/* audit medium — the full lockup ("TrueCare Partners") is
                    `shrink-0` and collided with the account name at 390px. Show
                    the short lockup (no "Partners" tail) on mobile, full at sm+. */}
                {/* Audit L4 (2026-09-01) — render the wordmark at md (matching
                    portal/marketing) so admin doesn't read as a lower-fidelity
                    bolted-on tool; the tighter header padding delivers the
                    density difference, not a shrunken lockup. Mobile keeps sm to
                    fit the 390px viewport. */}
                <Brand size="sm" short className="sm:hidden" />
                <Brand size="md" className="hidden sm:inline-flex" />
              </Link>
              {/* "Admin" as a subtle secondary badge (not by shrinking the
                  wordmark); dropped on mobile so it doesn't overflow 390px. */}
              <span className="hidden rounded bg-ink-soft/40 px-1.5 py-0.5 font-body text-xs font-medium text-ink-muted sm:inline">
                Admin
              </span>
            </div>
          </div>
          {user.data && (
            <Menu
              // Audit Tier-3 a11y (WCAG 2.5.3 Label in Name): the accessible
              // name must contain the trigger's VISIBLE text (the display name).
              label={`${user.data.displayName || user.data.email} — account menu`}
              trigger={
                <span
                  className="max-w-[9rem] truncate sm:max-w-[12rem]"
                  title={user.data.email}
                >
                  {user.data.displayName || user.data.email}
                </span>
              }
            >
              <Link to="/me" className={MENU_ITEM_CLASS}>
                My profile
              </Link>
              <button type="button" className={MENU_ITEM_CLASS} onClick={handleSignOut}>
                Sign out
              </button>
            </Menu>
          )}
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl">
        {/* Persistent sidebar — desktop only */}
        <aside className="hidden w-56 shrink-0 border-r border-border bg-white md:block">
          <div className="sticky top-[57px] max-h-[calc(100vh-57px)] overflow-y-auto">
            <NavList />
          </div>
        </aside>

        <main
          id="main-content"
          tabIndex={-1}
          className="min-w-0 flex-1 px-4 py-8 focus:outline-none md:px-6"
        >
          {children}
        </main>
      </div>

      {/* Off-canvas drawer — mobile only */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-ink/40"
            aria-hidden="true"
            onClick={() => setDrawerOpen(false)}
          />
          <aside
            ref={drawerRef}
            className="absolute left-0 top-0 h-full w-64 overflow-y-auto border-r border-border bg-white shadow-lg"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <span className="font-heading text-forest">Menu</span>
              <button
                ref={drawerCloseRef}
                type="button"
                className="rounded p-1 text-ink-muted transition-colors hover:bg-cream"
                aria-label="Close navigation"
                onClick={() => setDrawerOpen(false)}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
                  <path
                    d="M5 5l10 10M15 5L5 15"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <NavList onNavigate={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}
    </div>
  );
}
